import type { CityAutocompleteResult } from '@/app/home/(user)/_components/location-autocomplete';

const GEOCODE_TIMEOUT_MS = 10000;

/**
 * Reverse geocode coordinates to city/country using Google Maps Geocoding API.
 * Uses importLibrary('geocoding') which is required with loading=async.
 * Includes a timeout to prevent hanging if the API doesn't respond.
 *
 * Note: Requires the "Geocoding API" to be enabled in Google Cloud Console
 * (separate from Maps JavaScript API and Places API).
 *
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns Promise with city, country, countryCode, placeId, and coordinates
 * @throws Error if geocoding fails, times out, or Google Maps API is not loaded
 */
export async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<CityAutocompleteResult> {
  if (typeof window === 'undefined' || !window.google?.maps?.importLibrary) {
    throw new Error('Google Maps API is not loaded');
  }

  // Import the geocoding library (required with loading=async)
  const geocodingLib = (await window.google.maps.importLibrary(
    'geocoding',
  )) as {
    Geocoder: new () => {
      geocode: (request: {
        location: { lat: number; lng: number };
      }) => Promise<{
        results: Array<{
          types: string[];
          formatted_address: string;
          place_id: string;
          address_components: Array<{
            types: string[];
            long_name: string;
            short_name: string;
          }>;
        }>;
      }>;
    };
  };

  if (!geocodingLib?.Geocoder) {
    throw new Error('Geocoding library failed to load');
  }

  const geocoder = new geocodingLib.Geocoder();
  const latLng = { lat, lng };

  // Add timeout to prevent hanging forever
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(
      () => reject(new Error('Geocoding request timed out')),
      GEOCODE_TIMEOUT_MS,
    );
  });

  const response = await Promise.race([
    geocoder.geocode({ location: latLng }),
    timeoutPromise,
  ]);

  // Handle undefined/null response (happens when Geocoding API is not enabled)
  const results = response?.results;
  if (!results || results.length === 0) {
    throw new Error('Unable to find location for these coordinates');
  }

  // Find the result with the most specific locality information
  // Prefer results with 'locality' (city) type
  const cityResult =
    results.find((result) => result.types.includes('locality')) || results[0];

  if (!cityResult) {
    throw new Error('No valid location found');
  }

  let city = '';
  let country = '';
  let countryCode = '';

  // Extract city and country from address components
  if (cityResult.address_components) {
    for (const component of cityResult.address_components) {
      // Try locality first (city), fallback to administrative_area_level_1 (state/province)
      if (component.types.includes('locality')) {
        city = component.long_name;
      } else if (
        !city &&
        component.types.includes('administrative_area_level_1')
      ) {
        city = component.long_name;
      }

      if (component.types.includes('country')) {
        country = component.long_name;
        countryCode = component.short_name;
      }
    }
  }

  // Fallback to formatted_address if city not found
  if (!city && cityResult.formatted_address) {
    city = cityResult.formatted_address.split(',')[0] || '';
  }

  if (!city || !country) {
    throw new Error(
      'Unable to extract city and country from location coordinates',
    );
  }

  return {
    city,
    country,
    countryCode,
    placeId: cityResult.place_id,
    latitude: lat,
    longitude: lng,
  };
}
